import { useCallback, useContext, useRef, useState } from 'react'
import { useIntl } from 'react-intl'

import { useAppDispatch, shallowEqual, useAppSelector } from 'hooks/reduxHooks'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import uuidV4 from 'lib/string/uuidV4Utils'
import CurrencyContext from 'contexts/currencyContext'
import GeoContext from 'contexts/geoContext'
import { getCartMachineActor } from 'machines/cart/MultiCartMachineProvider'
import { transformUserCartItemsToCheckoutItems } from 'lib/checkout/userCart'
import { initialiseCheckout } from 'actions/CheckoutActions'
import { navigateToCheckout } from 'checkout/actions/navigateActions'
import { getOffersById } from 'api/offer'
import { reportLog } from 'services/userMonitoring'
import { fetchCharterBundleFlightItems, resolveCharterBundle, type CharterBundleHydrationResult, type CharterFlightBundleSelection } from 'tripPlanner/utils/charterBundleHydration'
import { isMaldivesCharterRegionEnabled } from 'selectors/maldivesSelectors'
import { useShouldWaiveLuxPlusJoiningFee } from 'hooks/UserCart/useCartItems'
import { isExperienceItem, isInstantBookingLEHotelItem } from 'lib/checkout/checkoutUtils'
import { isMultiCartFlightItem } from 'components/Pages/UserCartPage/SharedCarts/multiCartUtils'
import type { CartPanelItem } from './useCartPanelItems'
import {
  getCartPanelExperienceTimesKey,
  isCartPanelItemCheckoutReady,
  prepareCartPanelExperienceCheckoutItem,
} from '../utils/cartPanelUtils'
import { refreshTripChatBedbankItemSessions } from '../utils/tripChatCheckoutBedbankUtils'

interface UseTripChatCheckoutActionsArgs {
  chatId: number | undefined
  multiCartId: string | undefined
  refetchCartItems: () => Promise<Map<string, App.MultiCartItem>>
}

interface UseTripChatCheckoutActionsReturn {
  checkoutCartItems: (items: Array<CartPanelItem>) => Promise<void>
  isCheckingOut: boolean
}

function isCheckoutReadyItem(item: CartPanelItem): item is CartPanelItem & { cartItem: App.MultiCartItem } {
  return isCartPanelItemCheckoutReady(item) && !!item.cartItem
}

function withFreshCartItemSnapshot(
  item: CartPanelItem,
  cartItems: Map<string, App.MultiCartItem>,
): CartPanelItem {
  if (!item.cartItemId) return item
  const cartItem = cartItems.get(item.cartItemId)
  if (item.productType === 'flight') {
    return {
      ...item,
      cartItem,
      flightCartItem: cartItem && isMultiCartFlightItem(cartItem) ? cartItem : undefined,
    }
  }

  return {
    ...item,
    cartItem,
  }
}

function toCheckoutItem(
  item: CartPanelItem & { cartItem: App.MultiCartItem },
  currency: string,
  experiences: App.State['experience']['experiences'],
  experienceTimes: App.State['experience']['experienceTimes'],
): App.Checkout.AnyItem {
  const checkoutItem = {
    ...item.cartItem.item,
    itemId: item.cartItem.item.itemId ?? item.cartItem.id,
    transactionKey: item.cartItem.item.transactionKey ?? uuidV4(),
  }
  if (item.productType !== 'experience' || !isExperienceItem(checkoutItem)) return checkoutItem

  const key = getCartPanelExperienceTimesKey(checkoutItem, currency)
  return prepareCartPanelExperienceCheckoutItem(
    checkoutItem,
    experiences[checkoutItem.experienceId],
    experienceTimes[checkoutItem.experienceId]?.[key]?.slots,
  )
}

export function useTripChatCheckoutActions({
  chatId,
  multiCartId,
  refetchCartItems,
}: UseTripChatCheckoutActionsArgs): UseTripChatCheckoutActionsReturn {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const currency = useContext(CurrencyContext)
  const { currentRegionCode } = useContext(GeoContext)
  const shouldWaive = useShouldWaiveLuxPlusJoiningFee()
  const checkoutInFlightRef = useRef(false)
  const [isCheckingOut, setIsCheckingOut] = useState(false)
  const { subscriptionOffers, experiences, experienceTimes, email } = useAppSelector(state => ({
    subscriptionOffers: state.luxPlus.products.subscription.offers,
    experiences: state.experience.experiences,
    experienceTimes: state.experience.experienceTimes,
    email: state.auth?.account?.email,
  }), shallowEqual)
  const isMaldivesCharterEnabled = useAppSelector(isMaldivesCharterRegionEnabled)

  const defaultErrorMessage = intl.formatMessage({
    id: 'EmZPR-KxU7oRFczGeJk2',
    defaultMessage: 'Something went wrong. Please try again.',
    description: 'Default error message when a chat streaming request fails',
  })

  const charterBundleErrorMessage = intl.formatMessage({
    id: 'oS-wuNEBq6t8u5nGT-2z',
    defaultMessage: 'The included flight isn’t available for your selected dates. Please adjust your stay and try again.',
    description: 'Error shown when the bundled charter flight cannot be added at checkout',
  })

  const checkoutCartItems = useCallback(async(items: Array<CartPanelItem>) => {
    if (!chatId || !multiCartId || checkoutInFlightRef.current) return
    checkoutInFlightRef.current = true
    setIsCheckingOut(true)

    try {
      let freshCartItems: Map<string, App.MultiCartItem>
      try {
        freshCartItems = await refetchCartItems()
      } catch {
        showSnackbar(defaultErrorMessage, 'critical')
        return
      }

      const activeItems = items
        .map(item => withFreshCartItemSnapshot(item, freshCartItems))
        .filter(isCheckoutReadyItem)
      if (activeItems.length === 0) return

      getCartMachineActor()?.send({ type: 'set active cart', cartId: multiCartId })

      const checkoutSource: App.CheckoutSource = {
        type: 'trip_planner',
        multiCartId,
        externalReferenceId: String(chatId),
      }
      const checkoutItems = activeItems.map(item => toCheckoutItem(item, currency, experiences, experienceTimes))

      // Charter flight bundles (Maldives MEL↔MLE direct): detect each charter
      // bundle from the live offer (shared helper; requireFlightsEnabled is false
      // at checkout because getOffersById doesn't reliably populate it) and derive
      // its charter (+ feeder) flight legs, then inject them so checkout opens with
      // the bundled flight. Batch the offer fetch so we don't fire one request per
      // hotel on the checkout-click path.
      const accommodationOfferIds = [...new Set(
        activeItems.flatMap(item =>
          item.productType === 'accommodation' && item.offerId ? [item.offerId] : [],
        ),
      )]
      const offersById = new Map<string, App.AnyOffer>()
      if (accommodationOfferIds.length > 0) {
        try {
          const offers = await getOffersById(accommodationOfferIds, currentRegionCode)
          for (const offer of offers) {
            if ('id' in offer) offersById.set(offer.id, offer)
          }
        } catch { /* per-item missing-offer guard below decides whether to block */ }
      }

      const charterBundleItems: Array<{
        offer: App.HotelOffer | App.HotelOfferSummary
        charterFlightBundle: CharterFlightBundleSelection
        occupancies: Array<App.Occupants>
        checkIn: string | undefined
        checkOut: string | undefined
        checkoutItem: App.Checkout.InstantBookingLEHotelItem
      }> = []
      for (let i = 0; i < activeItems.length; i++) {
        const item = activeItems[i]
        if (item.productType !== 'accommodation' || !item.offerId) continue
        const offer = offersById.get(item.offerId)
        // Missing offer: getOffersById dropped this one (it can return a subset),
        // so we can't inspect it to rule out a charter bundle. Fail closed only for
        // items that could actually carry a charter flight — a persisted selection,
        // or an instant-booking LE hotel (the only item type charter bundles ride
        // on). Unrelated accommodation (bedbank/SPP/villa, which getOffersById may
        // not even return) checks out normally instead of blocking the whole cart.
        if (!offer) {
          if (isMaldivesCharterEnabled && (item.charterFlightBundle || isInstantBookingLEHotelItem(checkoutItems[i]))) {
            showSnackbar(charterBundleErrorMessage, 'critical')
            return
          }
          continue
        }
        const resolved = resolveCharterBundle({
          offer,
          isMaldivesCharterEnabled,
          persistedBundle: item.charterFlightBundle,
          occupancies: item.occupancies,
          requireFlightsEnabled: false,
        })
        if (!resolved) {
          if (item.charterFlightBundle) {
            showSnackbar(charterBundleErrorMessage, 'critical')
            return
          }
          continue
        }
        // The charter legs must ride on an instant-booking LE hotel item; if a
        // detected bundle isn't one we can't build its flight, so block checkout
        // rather than silently opening it flight-less.
        const checkoutItem = checkoutItems[i]
        if (!isInstantBookingLEHotelItem(checkoutItem)) {
          showSnackbar(charterBundleErrorMessage, 'critical')
          return
        }
        charterBundleItems.push({
          offer: resolved.offer,
          charterFlightBundle: resolved.charterFlightBundle,
          occupancies: resolved.occupancies,
          checkIn: item.checkIn,
          checkOut: item.checkOut,
          checkoutItem,
        })
      }

      // Hydrate every charter bundle's flight legs in parallel (one search per hotel).
      let charterResults: Array<CharterBundleHydrationResult>
      try {
        charterResults = await Promise.all(charterBundleItems.map(({ offer, charterFlightBundle, occupancies, checkIn, checkOut, checkoutItem }) =>
          fetchCharterBundleFlightItems({
            offer,
            charterFlightBundle,
            accommodationItems: [checkoutItem],
            checkIn,
            checkOut,
            occupancies,
            region: currentRegionCode,
            currency,
            email,
          }),
        ))
      } catch(error) {
        reportLog('Trip chat charter bundle hydration threw', {
          error,
          chatId,
          multiCartId,
          offerIds: charterBundleItems.map(({ offer }) => offer.id),
        }, 'error')
        showSnackbar(charterBundleErrorMessage, 'critical')
        return
      }

      const charterFlightItems: Array<App.Checkout.FlightItem> = []
      let hydrationFailed = false
      for (let i = 0; i < charterResults.length; i++) {
        const result = charterResults[i]
        if (!result.ok) {
          hydrationFailed = true
          const { offer, charterFlightBundle, checkIn, checkOut } = charterBundleItems[i]
          reportLog('Trip chat charter bundle hydration failed', {
            reason: result.reason,
            offerId: offer.id,
            charterBundleRoute: charterFlightBundle.charterBundleRoute,
            originAirportCode: charterFlightBundle.originAirportCode,
            fareClass: charterFlightBundle.fareClass,
            checkIn,
            checkOut,
            chatId,
            multiCartId,
          }, 'error')
          continue
        }
        charterFlightItems.push(...result.flightItems)
      }
      if (hydrationFailed) {
        showSnackbar(charterBundleErrorMessage, 'critical')
        return
      }

      let transformed = transformUserCartItemsToCheckoutItems(checkoutItems, subscriptionOffers, shouldWaive)
      try {
        transformed = await refreshTripChatBedbankItemSessions(transformed, dispatch)
      } catch {
        showSnackbar(defaultErrorMessage, 'critical')
        return
      }

      const allItems = [...transformed, ...charterFlightItems]
      dispatch(initialiseCheckout(allItems.map(item => ({ ...item, transactionKey: uuidV4() })), { checkoutSource }, true))
      dispatch(navigateToCheckout({ skipSpoofedModal: true }))
    } finally {
      checkoutInFlightRef.current = false
      setIsCheckingOut(false)
    }
  }, [chatId, multiCartId, refetchCartItems, defaultErrorMessage, charterBundleErrorMessage, currency, currentRegionCode, email, isMaldivesCharterEnabled, experiences, experienceTimes, subscriptionOffers, shouldWaive, dispatch])

  return { checkoutCartItems, isCheckingOut }
}
